import { SearchInput } from '@/components/SearchInput';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useLiveGraphQL } from '@/hooks/useLiveGraphQL';
import { alert } from '@/services/alert';
import { ORGANIZATION_TYPE_CODING_MAP } from '@bariendo/lib/codings';
import { ORGANIZATIONS_EXPAND_DRAWER_QUERY_PARAM, OrganizationType } from '@bariendo/lib/constants';
import {
  ActionIcon,
  Box,
  Button,
  Container,
  Flex,
  Group,
  Paper,
  Select,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { useDebouncedState } from '@mantine/hooks';
import { createReference } from '@medplum/core';
import { ContactPoint, Organization, Reference } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { formatPhoneNumber } from '@bariendo/lib/phone-validations';
import { IconPlus, IconSearch, IconTrash } from '@tabler/icons-react';
import { MantineReactTable, MRT_ColumnDef, useMantineReactTable } from 'mantine-react-table';
import { ReactElement, useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { ContactsAvatarGroup } from './ContactsAvatarGroup';
import { ORGANIZATIONS_QUERY, PartnerOrganizationsResource } from './graphql';
import { PartnerCreateDrawer } from './PartnerCreateDrawer';
import { PartnerEditDrawer } from './PartnerEditDrawer';
import classes from './PartnersPage.module.css';
import { PartnerTypePill } from './PartnerTypePill';
import { PartnersPageSkeleton } from './PartnersPageSkeleton';
import { SystemLink } from './SystemLink';

function getFirstEmail(org: Organization): ContactPoint | undefined {
  return org.telecom?.find((t) => t.system === 'email');
}

function getFirstPhone(org: Organization): ContactPoint | undefined {
  return org.telecom?.find((t) => t.system === 'phone');
}

type DrawerState = {
  org?: PartnerOrganizationsResource;
  action?: 'create' | 'edit';
};

type DrawerAction = { type: 'create' } | { type: 'edit'; org: PartnerOrganizationsResource } | { type: 'reset' };

function drawerReducer(state: DrawerState, action: DrawerAction): DrawerState {
  switch (action.type) {
    case 'create':
      return { org: undefined, action: 'create' };
    case 'edit':
      return { org: action.org, action: 'edit' };
    case 'reset':
    default:
      return { org: undefined, action: undefined };
  }
}

type PartnersPageFilters = {
  type?: OrganizationType;
  search?: string;
};

export function PartnersPage(): ReactElement {
  const { currentUserPermissions, permissionsLoading } = useCurrentUser();
  const navigate = useNavigate();
  // Drawer controls
  const [state, dispatcher] = useReducer(drawerReducer, {
    org: undefined,
    action: undefined,
  });

  // Search + filters
  const [filters, setFilters] = useDebouncedState<PartnersPageFilters>({}, 200);
  const medplum = useMedplum();
  const projectId = medplum.getProject()?.id;

  const variables = useMemo(() => {
    const v: Record<string, any> = {};
    const filterParts: string[] = [];

    if (filters.type) v.type = filters.type;
    if (filters.search) filterParts.push(`name co "${filters.search}"`);
    if (projectId) filterParts.push(`_project eq "${projectId}"`);

    const filterString = filterParts.join(' and ');
    if (filterString) v.name = filterString;

    return Object.keys(v).length > 0 ? v : undefined;
  }, [filters.type, filters.search, projectId]);

  // Subscription criteria for creating subscription
  const subscriptionCriteria = {
    type: filters.type,
    'name:contains': filters.search,
  };

  // Orgs + selected org (drawer) state control
  const {
    data: orgsData,
    loading,
    hasLoadedInitialData,
    scrollPagination,
  } = useLiveGraphQL<PartnerOrganizationsResource>(ORGANIZATIONS_QUERY, variables, {
    subscriptionParams: subscriptionCriteria,
    enablePagination: true,
    pageSize: 30,
    addNewItemToTop: true,
  });

  const [searchParams] = useSearchParams();
  const queryOrgId = searchParams.get(ORGANIZATIONS_EXPAND_DRAWER_QUERY_PARAM);
  const lastExpandedQueryOrgId = useRef<string | null>(null);
  const orgRef = useRef<Reference<Organization> | undefined>(undefined);

  useEffect(() => {
    if (!loading && orgsData?.length > 0 && state.action === 'edit') {
      const selectedOrg = orgsData.find((org) => org.id === state.org?.id);
      dispatcher({ type: 'edit', org: selectedOrg! });
      if (selectedOrg) {
        orgRef.current = createReference(selectedOrg);
      }
    }
  }, [orgsData, loading, state.action, state.org?.id]);

  useEffect(() => {
    // Open partner drawer from URL query parameter
    if (queryOrgId && !loading && orgsData && lastExpandedQueryOrgId.current !== queryOrgId) {
      lastExpandedQueryOrgId.current = queryOrgId;
      const targetOrg = orgsData.find((org) => org.id === queryOrgId);

      if (targetOrg) {
        dispatcher({ type: 'edit', org: targetOrg });
        orgRef.current = createReference(targetOrg);
      } else {
        alert.error(`Organization with ID "${queryOrgId}" not found`);
      }
    }
  }, [queryOrgId, loading, orgsData]);

  const handleDeleteOrg = useCallback(
    // TODO: Add logic to delete organization from patient contacts
    async (orgId: string) => {
      await medplum.deleteResource('Organization', orgId);
    },
    [medplum]
  );

  const columns = useMemo<MRT_ColumnDef<PartnerOrganizationsResource>[]>(
    () => [
      {
        id: 'organization',
        header: 'Organization',
        accessorFn: (org) => org.name,
        Cell: ({ cell }) => <Text size="sm">{cell.getValue<string>()}</Text>,
      },
      {
        id: 'type',
        header: 'Type',
        enableSorting: false,
        accessorFn: (org) => org.type?.[0].coding?.[0].code || 'unknown',
        Cell: ({ cell }) => <PartnerTypePill type={cell.getValue<OrganizationType>()} />,
      },
      {
        id: 'phone',
        header: 'Phone',
        Cell: ({ row }) => (
          <SystemLink system="phone" value={getFirstPhone(row.original)?.value} className={classes.hoverLink}>
            <Text size="sm" style={{ color: 'inherit' }}>
              {formatPhoneNumber(getFirstPhone(row.original)?.value) || '-'}
            </Text>
          </SystemLink>
        ),
      },
      {
        id: 'email',
        header: 'Email',
        Cell: ({ row }) => <Text size="sm">{getFirstEmail(row.original)?.value || '-'}</Text>,
      },
      {
        id: 'contacts',
        header: 'Contacts',
        Cell: ({ row }) => (
          <>
            <Flex align="center">
              <Box
                style={{
                  zIndex: 1,
                  position: 'absolute',
                  right: 0,
                  marginRight: 20,
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  width: 'fit-content',
                  borderRadius: '10px',
                  border: '1px solid #e0e0e0',
                }}
                px={10}
                bg="white"
                py={5}
                onClick={(e) => e.stopPropagation()}
              >
                <Group>
                  <Tooltip label="Delete">
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      size="xs"
                      onClick={() => handleDeleteOrg(row.original.id!)}
                    >
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Box>
              <ContactsAvatarGroup contacts={row.original.contact || []} />
            </Flex>
          </>
        ),
      },
    ],
    [handleDeleteOrg]
  );

  const table = useMantineReactTable({
    columns,
    data: orgsData ?? [],
    rowCount: orgsData?.length ?? 0,
    getRowId: (row) => row.id ?? `row-${row.name}`,
    initialState: {
      density: 'xs' as const,
    },
    state: {
      showGlobalFilter: false,
      isLoading: loading,
    },
    enableRowSelection: false,
    enableSorting: true,
    enableColumnFilters: false,
    enableGlobalFilter: false,
    enablePagination: false,
    enableColumnActions: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableRowActions: false,
    enableRowVirtualization: (orgsData?.length ?? 0) > 25,
    enableSelectAll: false,
    enableToolbarInternalActions: false,
    mantineTableProps: {
      highlightOnHover: true,
    },
    mantineTableContainerProps: scrollPagination?.tableContainerProps,
    mantineTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        if (row.original.id) {
          navigate(`/Organizations/${row.original.id}/timeline`);
        }
      },
      style: { cursor: 'pointer' },
    }),
    mantineTableHeadCellProps: {
      style: {
        fontWeight: '500',
      },
    },
    mantinePaperProps: {
      shadow: 'sm',
      withBorder: true,
      style: { height: '100%', display: 'flex', flexDirection: 'column' },
    },
    renderEmptyRowsFallback: () => (
      <Text size="sm" c="dimmed" ta="center" py="md">
        No results found
      </Text>
    ),
  });

  useEffect(() => {
    if (!permissionsLoading && !currentUserPermissions.canAccessPartners) {
      alert.error({
        title: 'Access denied',
        message: (
          <Text>
            You don't have permission to view this page.
            <br />
            If you think this is a mistake, contact your administrator.
          </Text>
        ),
      });
      navigate('/', { replace: true });
    }
  }, [permissionsLoading, currentUserPermissions.canAccessPartners, navigate]);

  if (permissionsLoading || !currentUserPermissions.canAccessPartners) {
    return <></>;
  }

  return (
    <Container
      size="xl"
      py="md"
      style={{
        height: 'calc(100vh - 60px)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack gap="lg" style={{ flex: 1, overflow: 'hidden' }}>
        <Group justify="space-between" align="center">
          <Title order={1}>Partners</Title>
          <Group gap="md">
            <Select
              placeholder="Filter by type"
              onChange={(value) => setFilters({ ...filters, type: (value as OrganizationType) || undefined })}
              data={Object.values(ORGANIZATION_TYPE_CODING_MAP).map((coding) => ({
                value: coding.code as OrganizationType,
                label: coding.display!,
              }))}
              allowDeselect
            />
            <SearchInput
              leftSection={<IconSearch size={16} />}
              placeholder="Search partners..."
              onChange={(value) => setFilters({ ...filters, search: value })}
            />
            <Button variant="subtle" color="green" onClick={() => dispatcher({ type: 'create' })}>
              <Group gap="xs">
                <IconPlus size={16} />
                Add partner
              </Group>
            </Button>
          </Group>
        </Group>
        {!hasLoadedInitialData && orgsData?.length === 0 ? (
          <PartnersPageSkeleton />
        ) : (
          <Paper style={{ flex: 1, overflow: 'hidden' }}>
            <MantineReactTable table={table} />
          </Paper>
        )}
      </Stack>
      <PartnerCreateDrawer opened={state.action === 'create'} onClose={() => dispatcher({ type: 'reset' })} />
      <PartnerEditDrawer
        opened={state.action === 'edit'}
        onClose={() => dispatcher({ type: 'reset' })}
        orgRef={orgRef.current}
      />
    </Container>
  );
}
